import { createSelector } from 'reselect';
import { formValueSelector } from 'redux-form';

import { RootState } from '../../reducers';
import { PreviewPremix } from '../../entities/premixes/premixes.types';
import { EntryMethodsKeys, IngredientInPremixCreation, TargetWeights } from '../recipes/appRecipes.types';
import { MixingTypeCodes } from '../../../config/constants';
import { getWaterId } from '../../entities/ingredients/ingredients.selector';
import { Forms } from '../../form/form.selector';

export const getIngredientsInPremix = (state: RootState): IngredientInPremixCreation[] =>
  state.application.premixes.premixCreation.ingredientsInPremix;

export const getPremixPayload = (state: RootState) => state.application.premixes.premixPayload;

export const getIsEditMode = (state: RootState): boolean => state.application.premixes.premixCreation.isEditMode;
export const getMethod = (state: RootState): EntryMethodsKeys => state.application.premixes.premixCreation.method;

export const getTargetWeightType = (state: RootState): TargetWeights =>
  state.application.premixes.premixCreation.targetWeightType;

export const getTargetDryMatterWeight = (state: RootState): number =>
  state.application.premixes.premixCreation.targetDryMatterWeight;

export const getTargetActualLoadWeight = (state: RootState): number =>
  state.application.premixes.premixCreation.targetActualLoadWeight;

const premixAutoAdjWaterSelector = (state: RootState) =>
  formValueSelector(Forms.premixCreationForm)(state, 'autoAdjWater');

export const getSelectedIngredientInList = (state: RootState): string | null =>
  state.application.premixes.premixCreation.selectedIngredientInList;

export const getIngredientsInPremixListAreValid = createSelector(
  [getIngredientsInPremix, premixAutoAdjWaterSelector, getWaterId],
  (ingredientsInPremix: IngredientInPremixCreation[], autoAdjWater: boolean, waterId: string): boolean => {
    if (ingredientsInPremix.length === 0) {
      return false;
    }
    return ingredientsInPremix.every(
      (ing) =>
        (autoAdjWater && ing.id === waterId && ing.actualLoad >= 0) ||
        (ing.actualLoad > 0 &&
          (ing.mixerData.mixingTypeCode === MixingTypeCodes.none || ing.mixerData.mixingValue !== 0)),
    );
  },
);

export const getIngredientsInPremixListAreValidAtLeastOne = createSelector(
  [getIngredientsInPremix],
  (ingredientsInPremix: IngredientInPremixCreation[]) => {
    if (ingredientsInPremix.length === 0) {
      return false;
    }
    return ingredientsInPremix.some((ing) => ing.actualLoad > 0);
  },
);

export const hasWater = createSelector(
  [getIngredientsInPremix, getWaterId],
  (ingredients: IngredientInPremixCreation[], waterId: string | undefined) => {
    return ingredients.some((elem) => elem.id === waterId);
  },
);

export const hasIngredientsInPremix = createSelector(
  [getIngredientsInPremix],
  (ingredientsInPremix): boolean => !!ingredientsInPremix.length,
);

export const getPreviewPremix = (state: RootState): PreviewPremix | null =>
  state.application.premixes.premixCreation.previewPremix;

export const getFinalDryMatterAverage = createSelector([getPreviewPremix], (preview: PreviewPremix) =>
  preview ? preview.finalDryMatterPerc : 0,
);

export const getTotalActualLoadAmounts = createSelector([getPreviewPremix], (preview: PreviewPremix) =>
  preview ? preview.finalAmount : 0,
);

export const getTotalDryMatterAmounts = createSelector(
  [getIngredientsInPremix],
  (ingredientsInPremix: IngredientInPremixCreation[]) =>
    ingredientsInPremix.reduce((acc, ing) => acc + ing.dryMatter, 0),
);

export const getTotalDryMatterPercentage = createSelector(
  [getIngredientsInPremix],
  (ingredientsInRecipe: IngredientInPremixCreation[]) => {
    return ingredientsInRecipe.reduce((acc, ing) => acc + ing.dryMatterTotalPercentage, 0);
  },
);

export const getTotalActualLoadPercentage = createSelector(
  [getIngredientsInPremix],
  (ingredientsInRecipe: IngredientInPremixCreation[]) => {
    return ingredientsInRecipe.reduce((acc, ing) => acc + ing.actualLoadTotalPercentage, 0);
  },
);

export const isFullLoad = createSelector(
  [getTotalDryMatterPercentage, getTotalActualLoadPercentage],
  (dryMatterPercentage: number, actualLoadPercentage) => {
    return Math.round(dryMatterPercentage * 100) / 100 === 100 && Math.round(actualLoadPercentage * 100) / 100 === 100;
  },
);
